import {makeAutoObservable, observable, reaction, runInAction} from "mobx";
import {ViewController} from "data/types/structure";
import {injectable, inject} from "inversify";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {ModalType, GameLevel} from "data/enums";
import {Empty} from "data/types/global";
import {Bindings} from "data/constants/bindings";
import type {IModalsStore} from "data/stores/modals/modals.store";
import type {IGameBarStore, IGameBar} from "data/stores/game_bar/game_bar.store";
import {times, shuffle, last} from "lodash";
import type {IChallenge, IPool, IH2hStore} from "data/stores/h2h/h2h.store";
import type {IPubNubClientService} from "data/services/pub_nub/pub_nub_client.servise";
import type {IContestsStore} from "data/stores/contests/contestsStore";
import {extractErrorMessage, wait} from "data/utils";
import {OpponentRelationUserType, OpponentUserType, PoolStatus} from "data/enums/challenge";

interface IAnimationUser {
	id: number;
	level: GameLevel;
	userName: string;
	isBot: boolean;
	isFriend: boolean;
}

export interface IModalH2hChallengeController extends ViewController {
	close: () => void;

	get i18n(): ILocalizationStore;

	get isOpen(): boolean;

	get userGameBar(): Empty<IGameBar>;

	get animation(): string;

	get errorMessage(): string;

	get randomUsers(): IAnimationUser[];

	get lastUser(): Empty<IAnimationUser>;

	get opponentGameBar(): Empty<IAnimationUser>;
}

@injectable()
export class ModalH2hChallengeController implements IModalH2hChallengeController {
	@observable _challengeUser?: IChallenge;
	@observable _animationClassName: string = "default";
	@observable _pubNubPrefix: string = "mlb-fantasy-spinball-pool-";
	@observable _errorMessage: string = "";
	@observable _timeout?: ReturnType<typeof setTimeout>;
	private _disposers?: ReturnType<typeof reaction>[];
	@observable private _pubNubDispose?: ReturnType<IPubNubClientService["listen"]>;

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.ModalsStore) private readonly _modalsStore: IModalsStore,
		@inject(Bindings.GameBarStore) private readonly _gameBarStore: IGameBarStore,
		@inject(Bindings.ContestsStore) private readonly _contestStore: IContestsStore,
		@inject(Bindings.H2hStore) public readonly _h2hStore: IH2hStore,
		@inject(Bindings.PubNubClientService) public readonly _pubNubClient: IPubNubClientService
	) {
		makeAutoObservable(this);
	}

	get isOpen(): boolean {
		return this._modalsStore.modal === ModalType.H2H_CHALLENGE;
	}

	get userGameBar() {
		return this._gameBarStore.gameBar;
	}

	get opponentGameBar(): Empty<IAnimationUser> {
		if (this._challengeUser) {
			return {
				id: this._challengeUser.opponent.id,
				level: this._challengeUser.gamebar.level || GameLevel.SINGLE_A,
				userName: this._challengeUser.opponent.displayName,
				isFriend: this._challengeUser.opponentType === OpponentRelationUserType.FRIEND,
				isBot: this._challengeUser.opponent.type === OpponentUserType.BOT,
			};
		}
	}

	get contest() {
		return this._contestStore.currentContest;
	}

	get contestId(): number {
		return this.contest?.id ?? 0;
	}

	init(): void {
		this._listenToPubNub();

		this._disposers = [
			reaction(
				() => this.isOpen,
				(isOpen) => {
					runInAction(() => {
						this._challengeUser = undefined;
						this._errorMessage = "";
						this._animationClassName = "default";
					});
					if (isOpen) {
						void this.joinToPool();
					}
				}
			),
			reaction(
				() => this._h2hStore.activePoolId,
				(poolId) => {
					if (poolId) {
						this.subscribeToEvents(poolId);
						this._listenToPubNub();
					}
				}
			),
		];
	}

	dispose() {
		this._pubNubDispose?.();
		this._challengeUser = undefined;
		this.stopTimeout();
		this._disposers?.forEach((dispose) => dispose());
	}

	public close = () => {
		this._modalsStore.hideModal();
	};

	get animation(): string {
		if (this._challengeUser) {
			this.stopTimeout();
			return "stopAnimate";
		}
		return this._animationClassName;
	}

	get errorMessage(): string {
		return this._errorMessage;
	}

	get randomUsers(): IAnimationUser[] {
		return times(10, (n) => n).map((_, index) => {
			return {
				get level() {
					const levelsList = shuffle([
						GameLevel.SINGLE_A,
						GameLevel.HIGH_A,
						GameLevel.AA,
						GameLevel.AAA,
						GameLevel.MLB_CALL_UP,
						GameLevel.MLB_STARTER,
						GameLevel.MLB_ALL_STAR,
						GameLevel.MLB_HALL_OF_FAMER,
					]);
					return levelsList[0];
				},
				userName: `User ${index + 1}`,
				isBot: false,
				isFriend: false,
				id: 0,
			};
		});
	}

	get lastUser(): Empty<IAnimationUser> {
		return last(this.randomUsers);
	}

	joinToPool = async () => {
		await wait(1000);
		const contestId = this.contestId;
		runInAction(() => {
			this._animationClassName = "animate";
		});

		if (contestId) {
			void this._h2hStore
				.requestPoolId(contestId)
				.then((pool) => {
					if (pool.status === PoolStatus.CLOSED) {
						runInAction(() => {
							this._errorMessage = this.i18n.t(
								"h2h.modal.pool_closed",
								"Challenge request is closed"
							);
						});
					}
				})
				.then(() => {
					this.startTimeout();
				})
				.catch((e) => {
					runInAction(() => {
						this._errorMessage = extractErrorMessage(e);
					});
				});
		}
	};

	private subscribeToEvents(poolId: number) {
		const channel = `${this._pubNubPrefix}${poolId}`;
		this._pubNubClient.subscribe({
			channels: [channel],
		});
	}

	private unsubscribeFromEvents(poolId: number) {
		const channel = `${this._pubNubPrefix}${poolId}`;
		this._pubNubClient.unsubscribe({
			channels: [channel],
		});
	}

	private _listenToPubNub() {
		this._pubNubDispose = this._pubNubClient.listen<IPool>((messageEvent) => {
			if (!messageEvent.channel.includes(this._pubNubPrefix)) {
				return;
			}
			const pool = messageEvent.message;
			if (pool) {
				void this._h2hStore
					.requestChallengeByPool(pool.id)
					.then((challenge) => {
						runInAction(() => {
							this._challengeUser = challenge;
							this._h2hStore.clearActivePoolId();
						});
					})
					.catch((e) => {
						this._errorMessage = extractErrorMessage(e);
					});

				this.unsubscribeFromEvents(pool.id);
			}
		});
	}

	private startTimeout() {
		this.stopTimeout();
		this._timeout = setTimeout(() => {
			runInAction(() => {
				this._errorMessage = this.i18n.t("h2h.modal.timeout", "Opponent not found");
			});
		}, 30000);
	}

	private stopTimeout() {
		clearTimeout(this._timeout);
	}
}
